import {useEffect, useMemo, useState} from "react";
import {
	App,
	AutoComplete,
	Button,
	Card,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Space,
	Tooltip,
	Typography
} from "antd";
import {InfoCircleOutlined, ReloadOutlined, RobotOutlined, SaveOutlined, SyncOutlined} from "@ant-design/icons";
import {AI_MODEL_OPTIONS} from "@shared/aiModels.js";
import {fetchAvailableAiModels} from "../api/settingsApi.js";

const {Title, Text} = Typography;

export default function AiSettingsTab({loading, saving, isDirty, onReload})
{
	const {message} = App.useApp();
	// null finche' non e' mai stato caricato con successo: in quel caso l'AutoComplete usa
	// AI_MODEL_OPTIONS (elenco statico) come fallback, cosi' il campo resta utilizzabile anche
	// senza API key salvata o con OpenAI momentaneamente irraggiungibile.
	const [liveModelOptions, setLiveModelOptions] = useState(null);
	const [modelsLoading, setModelsLoading] = useState(false);
	// Testo di ricerca gestito a parte dal valore del campo (che e' gia' un modello salvato,
	// es. "gpt-5.6-luna"): con il filtro nativo di AutoComplete il popup si filtra sul valore
	// GIA' presente nel campo appena lo si apre, mostrando solo se stesso. Azzerandolo al focus
	// il menu riparte sempre dall'elenco completo; la ricerca mentre si digita resta invariata.
	const [modelSearch, setModelSearch] = useState("");

	const modelOptions = useMemo(() =>
	{
		const source = liveModelOptions || AI_MODEL_OPTIONS;
		const needle = modelSearch.trim().toLowerCase();
		if (!needle)
			return source;
		return source.filter(option => String(option.value || "").toLowerCase().includes(needle));
	}, [liveModelOptions, modelSearch]);

	async function loadAvailableModels()
	{
		setModelsLoading(true);
		try
		{
			const models = await fetchAvailableAiModels();
			setLiveModelOptions(models.map(value => ({value, label: value})));
		}
		catch (error)
		{
			message.error(error?.response?.data?.error || "Impossibile recuperare l'elenco modelli da OpenAI");
		}
		finally
		{
			setModelsLoading(false);
		}
	}

	useEffect(() =>
	{
		loadAvailableModels();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="settings-tab-content">
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Card
						className="settings-content-card card-elev"
						actions={[
							<div key="actions" style={{display: "flex", justifyContent: "flex-end", gap: "8px", padding: "0 16px"}}>
								<Button
									icon={<ReloadOutlined spin={loading}/>}
									onClick={onReload}
									disabled={loading}
								>
									Ricarica
								</Button>
								<Button
									type="primary"
									htmlType="submit"
									icon={<SaveOutlined/>}
									loading={saving}
									disabled={!isDirty && !saving}
								>
									Salva
								</Button>
							</div>
						]}
					>
						<div className="settings-section-header">
							<div className="settings-section-icon-wrapper ai">
								<RobotOutlined className="settings-section-icon"/>
							</div>
							<div>
								<Title level={5} className="settings-section-title">
									Configurazione AI
								</Title>
								<Text type="secondary" className="settings-section-desc">
									Credenziali e comportamento dell&apos;assistente
								</Text>
							</div>
						</div>

						<div className="settings-form-section">
							<Form.Item
								label={
									<Space>
										<span>Chiave API OpenAI</span>
										<Tooltip title="La chiave viene salvata in modo sicuro e criptato nel database">
											<InfoCircleOutlined className="settings-tooltip-icon"/>
										</Tooltip>
									</Space>
								}
								name={["ai", "apiKey"]}
							>
								<Input.Password
									placeholder="sk-..."
									autoComplete="off"
								/>
							</Form.Item>
						</div>

						<div className="settings-form-section">
							<Form.Item
								label={
									<Space>
										<span>Modello predefinito</span>
										<Tooltip title={liveModelOptions ? "Elenco recuperato da OpenAI con la API key salvata: aggiorna" : "Recupera l'elenco dei modelli disponibili con la API key salvata"}>
											<Button
												type="text"
												size="small"
												icon={<SyncOutlined spin={modelsLoading}/>}
												onClick={loadAvailableModels}
												disabled={modelsLoading}
												style={{padding: "0 4px"}}
											/>
										</Tooltip>
									</Space>
								}
								name={["ai", "defaultModel"]}
								extra={liveModelOptions
									? "Modello utilizzato per le richieste AI. Una cooperativa può sovrascriverlo dalla pagina Organizzazione. Elenco aggiornato da OpenAI."
									: "Modello utilizzato per le richieste AI. Una cooperativa può sovrascriverlo dalla pagina Organizzazione. Elenco di suggerimenti statico: salva prima la API key per vedere solo i modelli davvero disponibili."}
							>
								<AutoComplete
									options={modelOptions}
									filterOption={false}
									onSearch={setModelSearch}
									onFocus={() => setModelSearch("")}
									placeholder="gpt-5-nano"
									allowClear
								/>
							</Form.Item>
						</div>

						<Divider className="settings-divider">
							<span className="settings-divider-text">Personalizzazione</span>
						</Divider>

						<div className="settings-form-section">
							<Form.Item
								label={
									<Space>
										<span>System Prompt</span>
										<Tooltip title="Questo prompt viene anteposto a tutte le richieste AI per definire il contesto e il comportamento">
											<InfoCircleOutlined className="settings-tooltip-icon"/>
										</Tooltip>
									</Space>
								}
								name={["ai", "systemPrompt"]}
								extra="Definisci il contesto, il tono di voce e le linee guida per le risposte AI"
							>
								<Input.TextArea
									className="settings-textarea-auto"
									placeholder="Sei un assistente specializzato nell'analisi di volantini promozionali..."
									autoSize={{minRows: 4, maxRows: 12}}
								/>
							</Form.Item>
						</div>
					</Card>
				</Col>
			</Row>
		</div>
	);
}
